import { FormControl } from '@/components/ui/form'
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { FormFieldProps, FormFileType } from './type'
import Cropper, { ReactCropperElement } from 'react-cropper'
import 'cropperjs/dist/cropper.css'
import { Button } from '@/components/ui/button'
import { CropIcon, DownloadIcon, Trash2Icon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Crop } from '@/type'
import { IImageAssetRO } from 'api'
import { useFormContext } from 'react-hook-form'

type FilePreview = {
  name: string
  src: string
  mimetype: string
  crop?: Crop
  base64?: string
  filename?: string
}

export type FileWrapper = {
  file: File
  crop?: Crop
  base64?: string
  uploadHandler?: (file: FileWrapper) => Promise<void>
}

export const FormFileField = React.memo(
  ({ data, field }: FormFieldProps<FormFileType>) => {
    const { watch } = useFormContext()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [openCropper, setOpenCropper] = useState(false)
    const cropperRef = useRef<ReactCropperElement>(null)
    const [cropTarget, setCropTarget] = useState<FilePreview | null>(null)
    const [cropTargetIndex, setCropTargetIndex] = useState<number | null>(null)

    const fieldValueWatch = watch(field.name)

    const onCrop = async () => {
      if (!cropTarget) {
        return
      }

      const crop = cropperRef.current!.cropper.getData()
      const res = await fetch(cropTarget.src)
      const blob = await res.blob()
      const file = new File([blob], cropTarget.name, {
        type: cropTarget.mimetype,
      })

      const base64 = cropperRef
        .current!.cropper.getCroppedCanvas()
        .toDataURL('base64')

      if (data.multiple) {
        field.onChange(
          field.value.map((value, index) =>
            index === cropTargetIndex
              ? { file, crop, base64, uploadHandler: data.uploadHandler }
              : value,
          ),
        )
      } else {
        field.onChange({
          file,
          crop,
          base64,
          uploadHandler: data.uploadHandler,
        })
      }

      setOpenCropper(false)
      setCropTarget(null)
      setCropTargetIndex(null)
    }

    const onOpenCropper = (preview: FilePreview, i: number) => {
      setOpenCropper(true)
      setCropTarget(preview)
      setCropTargetIndex(i)
      setTimeout(() => {
        if (preview.crop) {
          cropperRef.current?.cropper.setData({
            x: preview.crop.x,
            y: preview.crop.y,
            width: preview.crop.width,
            height: preview.crop.height,
          })
        }
      }, 10)
    }

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
      const files: FileWrapper[] = [...event.target.files!].map((f) => ({
        file: f,
        uploadHandler: data.uploadHandler,
      }))

      if (!files.length) {
        return
      }

      const values = data.multiple ? [...(field.value ?? []), ...files] : files

      field.onChange(data.multiple ? values : values[0])

      if (typeof data.crop === 'object' && data.crop?.autoOpen) {
        onOpenCropper(values[0], 0)
      }
      event.target.value = ''
    }

    const onRemove = (index: number) => {
      if (data.multiple) {
        const values = [...(field.value ?? [])]
        values.splice(index, 1)

        field.onChange(values)
      } else {
        field.onChange(null)
      }
    }

    const loadPreview = async (
      value: (IImageAssetRO | FileWrapper)[] | IImageAssetRO,
    ) => {
      if (!value) {
        return
      }

      const handleAsset = (asset: IImageAssetRO) => {
        arr.push({
          name: asset.id.toString(),
          mimetype: 'image/jpeg',
          src: asset.path,
        })
      }

      const handleFile = ({ file, crop, base64 }: FileWrapper) => {
        arr.push({
          name: file.name,
          mimetype: file.type,
          src: URL.createObjectURL(file),
          crop,
          base64,
        })
      }

      const arr: FilePreview[] = []
      if (Array.isArray(value)) {
        value.map((fileWrapper) => {
          if ('file' in fileWrapper) {
            handleFile(fileWrapper)
          } else {
            handleAsset(fileWrapper)
          }
        })
      } else {
        if ('file' in value) {
          handleFile(value)
        } else {
          handleAsset(value)
        }
      }
    }

    useEffect(() => {
      const subscription = watch((value, { name }) => {
        if (name === data.key) {
          loadPreview(value[name])
        }
      })

      return () => subscription?.unsubscribe()
    }, [data.key, watch])

    const previewFiles: FilePreview[] = useMemo(() => {
      const getFilePreview = (file: FileWrapper | IImageAssetRO) => {
        if (typeof file === 'object' && 'file' in file) {
          return {
            name: file.file.name,
            mimetype: file.file.type,
            src: URL.createObjectURL(file.file),
            crop: file.crop,
            base64: file.base64,
          }
        }

        return {
          name: file.originalFilename,
          mimetype: file.contentType,
          src: file.path,
        }
      }

      if (fieldValueWatch) {
        if (Array.isArray(fieldValueWatch)) {
          return fieldValueWatch.map(getFilePreview)
        }

        return [getFilePreview(fieldValueWatch)]
      }

      return []
    }, [fieldValueWatch])

    const onClickFileButton = () => {
      fileInputRef.current?.click()
    }

    return (
      <div className="space-y-2">
        <FormControl>
          {!data.readOnly && (
            <div>
              <Button type="button" size="sm" onClick={onClickFileButton}>
                파일 선택
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                className="sr-only"
                name={field.name}
                multiple={data.multiple}
                accept={data.accept}
                onChange={onChange}
              />
            </div>
          )}
        </FormControl>
        {previewFiles.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {previewFiles.map((p, i) => {
              return (
                <div className="relative block min-h-20 border" key={p.src}>
                  {p.mimetype?.startsWith('image') ? (
                    <>
                      <img
                        src={p.base64 || p.src}
                        draggable="false"
                        className="h-full min-h-20 object-contain"
                      />
                      <div className="absolute bottom-2 right-2 z-10 flex gap-2">
                        {!data.readOnly && (
                          <div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="flex h-auto p-1"
                              onClick={() => onOpenCropper(p, i)}
                            >
                              <CropIcon size={12} />
                            </Button>
                          </div>
                        )}
                        <a download={p.name} href={p.src}>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex h-auto p-1"
                          >
                            <DownloadIcon size={12} />
                          </Button>
                        </a>
                        {!data.readOnly && (
                          <Button
                            onClick={() => {
                              onRemove(i)
                            }}
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex h-auto p-1 text-red-500"
                          >
                            <Trash2Icon size={12} />
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="select-none overflow-hidden break-all p-2 text-xs text-muted-foreground">
                      <span className="line-clamp-6 block h-32">
                        {p.filename ?? p.name}
                      </span>

                      <div className="absolute bottom-2 right-2 z-10 flex gap-2">
                        <a download={p.filename ?? p.name} href={p.src}>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex h-auto p-1"
                          >
                            <DownloadIcon size={12} />
                          </Button>
                        </a>
                        {!data.readOnly && (
                          <Button
                            onClick={() => {
                              if (data.multiple) {
                                field.onChange(
                                  field.value.filter((_, index) => index !== i),
                                )
                              } else {
                                field.onChange([])
                              }
                            }}
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex h-auto p-1 text-red-500"
                          >
                            <Trash2Icon size={12} />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {data.imageAlertText && (
          <p className="text-sm text-blue-500">{data.imageAlertText}</p>
        )}
        <Dialog open={openCropper} onOpenChange={setOpenCropper} modal={false}>
          <DialogContent className="h-[700px] max-h-[80vh] min-w-[700px]">
            <DialogHeader>
              <DialogTitle>이미지 편집</DialogTitle>
            </DialogHeader>
            {previewFiles.length > 0 && cropTarget && (
              <Cropper
                className="row-span-12 min-h-0"
                viewMode={1}
                ref={cropperRef}
                src={cropTarget.src}
                aspectRatio={
                  typeof data.crop === 'object' && data.crop?.aspectRatio
                    ? data.crop.aspectRatio
                    : undefined
                }
                autoCropArea={1}
                rotatable={false}
                scalable={false}
                guides={false}
                zoomable={false}
              />
            )}
            <DialogFooter>
              <Button onClick={onCrop}>크롭하기</Button>
              <Button onClick={() => setOpenCropper(false)} variant="outline">
                닫기
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  },
)
